import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MediaCard from '@/components/base/MediaCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useFavorites } from '@/hooks/useFavorites';
import { getItemById, getRelated, typeLabel, categoryPath } from '@/utils/media';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const item = id ? getItemById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  usePageMeta(
    item ? `${item.title} - 久久精品影视详细介绍` : t('notFoundTitle'),
    item?.synopsis,
    item ? `久久久精品,${item.title},${item.genres.join(',')},影视详情` : undefined,
  );

  if (!item) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">{t('noResultTitle')}</h1>
        <p className="mt-2 text-sm text-foreground-500">{t('noResultDesc')}</p>
        <Link to="/" className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 cursor-pointer">
          {t('backHome')}
        </Link>
      </div>
    );
  }

  const related = getRelated(item, 5);
  const favorited = isFavorite(item.id);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast(t('shareSuccess'));
    } catch {
      setToast(t('shareSuccess'));
    }
    setTimeout(() => setToast(''), 2200);
  };

  const infoRows = [
    { label: t('director'), value: item.director, icon: 'ri-user-star-line' },
    { label: t('cast'), value: item.cast.join(' / '), icon: 'ri-group-line' },
    { label: t('genre'), value: item.genres.join(' · '), icon: 'ri-price-tag-3-line' },
    { label: t('region'), value: item.region, icon: 'ri-map-pin-line' },
    { label: t('releaseYear'), value: String(item.year), icon: 'ri-calendar-line' },
    item.type === 'movie'
      ? { label: t('duration'), value: item.duration, icon: 'ri-time-line' }
      : { label: t('episodes'), value: item.episodes, icon: 'ri-list-check' },
    { label: t('status'), value: item.status, icon: 'ri-signal-tower-line' },
  ];

  return (
    <div className="jjj-fade-up">
      <div className="relative">
        <div className="absolute inset-0 h-72 w-full overflow-hidden md:h-96">
          <img src={item.banner} alt={`${item.title}背景`} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/85 via-primary-950/90 to-background-50"></div>
        </div>

        <div className="relative mx-auto max-w-page px-4 pt-10 md:px-6 md:pt-14">
          <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-background-200/75">
            <Link to="/" className="transition hover:text-accent-300 cursor-pointer">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={categoryPath(item.type)} className="transition hover:text-accent-300 cursor-pointer">{typeLabel(item.type)}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">{item.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:items-end">
            <div className="h-64 w-44 shrink-0 overflow-hidden rounded-lg border-4 border-background-50 bg-background-200 md:h-80 md:w-56">
              <img
                src={item.cover}
                alt={`${item.title}封面`}
                title={`${item.title} ${typeLabel(item.type)} 详情封面`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 pb-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-500 px-2.5 py-1 text-xs font-semibold text-accent-950">{typeLabel(item.type)}</span>
                <span className="rounded-full bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100 backdrop-blur">{item.status}</span>
                <span className="flex items-center gap-1 rounded-full bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100 backdrop-blur">
                  <i className="ri-star-fill text-accent-400"></i>
                  {item.rating.toFixed(1)}
                </span>
              </div>
              <h1 className="mt-3 font-heading text-3xl font-extrabold text-background-50 md:text-4xl">{item.title}</h1>
              <p className="mt-2 text-sm text-background-100/80">{item.tagline}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(item.id)}
                  className={`flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition cursor-pointer whitespace-nowrap ${
                    favorited
                      ? 'bg-accent-500 text-accent-950 hover:bg-accent-400'
                      : 'bg-background-50/15 text-background-50 backdrop-blur hover:bg-background-50/25'
                  }`}
                >
                  <i className={`${favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-lg`}></i>
                  {favorited ? t('favorited') : t('addFavorite')}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 rounded-md border border-background-50/35 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line text-lg"></i>
                  {t('copyLink')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 pt-10 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[2fr_1fr]">
          <div className="flex flex-col gap-8">
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-file-text-line text-accent-500"></i>
                {t('synopsis')}
              </h2>
              <p className="rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-relaxed text-foreground-700">
                {item.synopsis}
              </p>
            </section>

            <section>
              <h2 className="mb-3 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-information-2-line text-accent-500"></i>
                {t('detailInfo')}
              </h2>
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className={`${row.icon} text-base`}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-400">{row.label}</p>
                      <p className="mt-0.5 text-sm font-medium text-foreground-900">{row.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-100 p-4 text-sm">
                <span className="flex items-center gap-2 font-medium text-foreground-700">
                  <i className="ri-folder-open-line text-accent-500"></i>
                  {t('categoryEntry')}
                </span>
                <Link
                  to={categoryPath(item.type)}
                  className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition hover:bg-primary-200 cursor-pointer"
                >
                  {typeLabel(item.type)}
                </Link>
                {item.genres.map((g) => (
                  <span key={g} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                    {g}
                  </span>
                ))}
              </div>
            </section>
          </div>

          <aside>
            <div className="sticky top-20 rounded-lg bg-gradient-to-br from-primary-600 to-primary-950 p-5 text-background-50">
              <h3 className="font-heading text-base font-bold">{t('detailInfo')}</h3>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between border-b border-background-50/15 pb-3">
                  <dt className="text-background-200/80 flex items-center gap-2"><i className="ri-star-line"></i>{t('rating')}</dt>
                  <dd className="font-heading text-lg font-bold text-accent-400">{item.rating.toFixed(1)}</dd>
                </div>
                <div className="flex items-center justify-between border-b border-background-50/15 pb-3">
                  <dt className="text-background-200/80 flex items-center gap-2"><i className="ri-map-pin-line"></i>{t('region')}</dt>
                  <dd>{item.region}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-background-200/80 flex items-center gap-2"><i className="ri-calendar-line"></i>{t('releaseYear')}</dt>
                  <dd>{item.year}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </div>

      <section className="mx-auto max-w-page px-4 py-12 md:px-6 md:py-16">
        <div className="mb-6">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{t('relatedTitle')}</h2>
          <p className="mt-1 text-sm text-foreground-500">{t('relatedDesc')}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {related.map((r) => (
            <MediaCard key={r.id} item={r} />
          ))}
        </div>
      </section>

      {toast ? (
        <div className="fixed bottom-24 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-primary-950 px-5 py-2.5 text-sm font-medium text-background-50 shadow-lg md:bottom-8">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-accent-400"></i>
            {toast}
          </span>
        </div>
      ) : null}
    </div>
  );
}